import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
    Portal,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    IconButton,
    TextField,
    Autocomplete,
    CircularProgress,
    Tooltip,
    Popper,
} from '@mui/material';
import CancelIcon from '@mui/icons-material/Cancel';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import DocumentService from '../../../Services/documents.service';
import EnumsService from '../../../Services/enums.service';
import { beautifyNumber } from '../../../Utils/FormatNumber';
import {
    Z_MODE_DAILY,
    Z_MODE_MONTHLY,
    parseZReportWorkbook,
    buildZReportEntries,
    summarizeZRows,
    formatUtcDate,
} from './zReportImport';
import fieldStyles from './AddJournalEntryModal.module.scss';
import modalStyles from './JournalEntryTemplatesModal.module.scss';
import styles from './CompoundJournalModals.module.scss';

const ComboPopper = React.forwardRef(({ direction = 'rtl', ...props }, ref) => (
    <Popper
        {...props}
        ref={ref}
        dir={direction}
        className={`${props.className || ''} ${fieldStyles.comboPopper}`}
        placement={direction === 'rtl' ? 'bottom-end' : 'bottom-start'}
        style={{ ...props.style, zIndex: 1600 }}
    />
));
ComboPopper.displayName = 'ComboPopper';

const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;
const toUtcNoonIso = (d) =>
    new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate(), 12)).toISOString();

/** Picker key → CaseSystemAccountSettings field that remembers it. */
const CARD_PICKERS = [
    { key: 'income', field: 'ZReportIncomeCardId', labelKey: 'ZReportIncomeCard', fallback: 'הכנסות קופה רושמת' },
    { key: 'customers', field: 'ZReportCustomersCardId', labelKey: 'ZReportCustomersCard', fallback: 'לקוחות קופה רושמת' },
    { key: 'cash', field: 'ZReportCashCardId', labelKey: 'ZReportCashCard', fallback: 'קופה מזומן', amountField: 'cash' },
    { key: 'checks', field: 'ZReportChecksCardId', labelKey: 'ZReportChecksCard', fallback: 'קופה שיקים', amountField: 'checks' },
    { key: 'creditCard', field: 'ZReportCreditCardCardId', labelKey: 'ZReportCreditCardCard', fallback: 'תקבולים אשראי', amountField: 'creditCard' },
];
const SORT_PICKER = { key: 'incomeSortCode', field: 'ZReportIncomeSortCode', labelKey: 'ZReportIncomeSortCode', fallback: 'קוד מיון הכנסות קופה רושמת' };
const ALL_PICKERS = [...CARD_PICKERS, SORT_PICKER];

const toCamel = (s) => s.charAt(0).toLowerCase() + s.slice(1);
const normName = (s) => String(s ?? '').replace(/["'״׳]/g, '').replace(/\s+/g, ' ').trim();
const REGISTER_RE = /קופ[הת] ?רושמת/;

/** First option whose name matches every pattern (used only when nothing was saved yet). */
const suggestByName = (options, getName, patterns) => {
    const hit = options.find((o) => patterns.every((p) => p.test(normName(getName(o)))));
    return hit ? String(hit.cardId ?? hit.sortCodeId) : '';
};

const ZReportImportModalContent = ({
    onClose,
    client,
    token,
    direction = 'rtl',
    accountCardOptions = [],
    sortCodeIdOptions = [],
    onCreated,
}) => {
    const { t } = useTranslation();
    const textAlign = direction === 'rtl' ? 'right' : 'left';
    const inputProps = useMemo(() => ({ style: { textAlign } }), [textAlign]);
    const comboInputProps = useMemo(() => ({ disableUnderline: true }), []);
    const renderComboPopper = useCallback(
        (props) => <ComboPopper {...props} direction={direction} />,
        [direction]
    );
    const fileInputRef = useRef(null);

    const [loadingSettings, setLoadingSettings] = useState(true);
    const [resolved, setResolved] = useState({});
    const [savedPicks, setSavedPicks] = useState({});
    const [picks, setPicks] = useState({});
    const [mode, setMode] = useState(Z_MODE_DAILY);
    const [fileName, setFileName] = useState('');
    const [parsed, setParsed] = useState(null);
    const [parsing, setParsing] = useState(false);
    const [saving, setSaving] = useState(false);

    const accountCards = useMemo(
        () => (accountCardOptions || []).filter((c) => c && c.cardId != null && c.cardId !== ''),
        [accountCardOptions]
    );
    const sortCodes = useMemo(
        () => (sortCodeIdOptions || []).filter((s) => s && s.sortCodeId != null && s.sortCodeId !== ''),
        [sortCodeIdOptions]
    );
    const cardOptions = useMemo(
        () => accountCards.map((c) => ({ value: String(c.cardId), label: `${c.cardId} - ${c.name || ''}` })),
        [accountCards]
    );
    const sortOptions = useMemo(
        () => sortCodes.map((s) => ({ value: String(s.sortCodeId), label: `${s.sortCodeId} - ${s.sortCodeName || ''}` })),
        [sortCodes]
    );
    const cardIds = useMemo(() => new Set(cardOptions.map((o) => o.value)), [cardOptions]);
    const sortIds = useMemo(() => new Set(sortOptions.map((o) => o.value)), [sortOptions]);

    useEffect(() => {
        if (!client?.caseId || !token) {
            setLoadingSettings(false);
            return undefined;
        }

        let cancelled = false;
        (async () => {
            try {
                const resp = await EnumsService.GetCaseSystemAccountSettings(client.caseId, token);
                if (!resp.ok) throw new Error(await resp.text());
                const data = await resp.json();
                if (cancelled) return;
                const r = data?.resolved || {};
                const saved = {};
                ALL_PICKERS.forEach(({ key, field }) => { saved[key] = String(r[toCamel(field)] ?? ''); });
                setResolved(r);
                setSavedPicks(saved);
            } catch (err) {
                console.error('Failed to load Z report settings:', err);
            } finally {
                if (!cancelled) setLoadingSettings(false);
            }
        })();
        return () => { cancelled = true; };
    }, [client?.caseId, token]);

    // Saved picks win; otherwise suggest by card / sort-code name, then the case's receipt vaults.
    useEffect(() => {
        if (loadingSettings) return;
        const cardName = (c) => c.name;
        const valid = (id, ids) => (id && ids.has(id) ? id : '');
        const suggestions = {
            income: suggestByName(accountCards, cardName, [/הכנסות/, REGISTER_RE]),
            customers: suggestByName(accountCards, cardName, [/לקוחות/, REGISTER_RE]),
            cash: suggestByName(accountCards, cardName, [/קופ[הת] ?מזומן/])
                || valid(String(resolved.acceptanceCashCardId ?? ''), cardIds),
            checks: suggestByName(accountCards, cardName, [/קופ[הת] ?(שיקים|שקים|צקים)/])
                || valid(String(resolved.acceptanceCheckCardId ?? ''), cardIds),
            creditCard: suggestByName(accountCards, cardName, [/תקבול/, /אשראי/])
                || valid(String(resolved.acceptanceCreditCardCardId ?? ''), cardIds),
            incomeSortCode: suggestByName(sortCodes, (s) => s.sortCodeName, [/הכנסות/, REGISTER_RE]),
        };
        setPicks((prev) => {
            const next = { ...prev };
            ALL_PICKERS.forEach(({ key }) => {
                if (next[key]) return;
                const ids = key === 'incomeSortCode' ? sortIds : cardIds;
                next[key] = valid(savedPicks[key], ids) || suggestions[key] || '';
            });
            return next;
        });
    }, [loadingSettings, savedPicks, resolved, accountCards, sortCodes, cardIds, sortIds]);

    const handleFile = async (file) => {
        if (!file) return;
        setParsing(true);
        try {
            const buffer = await file.arrayBuffer();
            const result = parseZReportWorkbook(new Uint8Array(buffer));
            setParsed(result);
            setFileName(file.name);
        } catch (err) {
            console.error('Z report parse failed:', err);
            const key = err?.message;
            toast.error(
                (key && t(key) !== key ? t(key) : null)
                || t('ZReportParseFailed')
                || 'Could not read the Z report file'
            );
        } finally {
            setParsing(false);
            if (fileInputRef.current) fileInputRef.current.value = '';
        }
    };

    const rows = useMemo(() => parsed?.rows || [], [parsed]);
    const totals = useMemo(() => summarizeZRows(rows), [rows]);

    const entries = useMemo(() => {
        if (!rows.length) return [];
        return buildZReportEntries(rows, {
            mode,
            cards: {
                income: picks.income,
                customers: picks.customers,
                cash: picks.cash,
                checks: picks.checks,
                creditCard: picks.creditCard,
            },
            sortCodes: {
                income: picks.incomeSortCode,
                exempt: String(resolved.sortCodeIncomeNoTax ?? '150'),
                debitCredit: String(resolved.sortCodeDebitCredit ?? '3'),
            },
            labels: {
                zReport: t('ZReportShort') || 'דו"ח Z',
                register: t('ZReportRegister') || 'קופה',
                monthlySummary: t('ZReportMonthlySummary') || 'ריכוז דו"ח Z',
            },
        });
    }, [rows, mode, picks, resolved, t]);

    const isRequired = useCallback(
        (picker) => !picker.amountField || totals[picker.amountField] !== 0,
        [totals]
    );
    const missingPickers = ALL_PICKERS.filter((p) => isRequired(p) && !picks[p.key]);
    const mismatchRows = rows.filter((r) => r.mismatch !== 0);
    const unbooked = round2(totals.onAccount + totals.other + totals.vouchers + totals.creditPoints);
    const receiptsTotal = round2(totals.cash + totals.checks + totals.creditCard);
    const canImport = entries.length > 0 && missingPickers.length === 0 && !saving && !loadingSettings;

    const cardLabel = (id) => cardOptions.find((o) => o.value === String(id))?.label || id || '—';
    const sortLabel = (id) => sortOptions.find((o) => o.value === String(id))?.label || id || '—';

    const persistPicks = async () => {
        const payload = {};
        ALL_PICKERS.forEach(({ key, field }) => {
            const value = picks[key] || '';
            if (value && value !== savedPicks[key]) payload[field] = value;
        });
        if (!Object.keys(payload).length) return;
        try {
            const resp = await EnumsService.UpdateCaseSystemAccountSettings(client.caseId, payload, token);
            if (!resp.ok) throw new Error(await resp.text());
        } catch (err) {
            console.error('Failed to remember Z report cards:', err);
            toast.warn(t('ZReportRememberCardsFailed') || 'The entries were imported, but the selected cards could not be saved for next time');
        }
    };

    const handleImport = async () => {
        if (!canImport) return;
        const uploadDate = new Date().toISOString();
        const payload = entries.map((e) => {
            const reportingIso = new Date(Date.UTC(e.date.getUTCFullYear(), e.date.getUTCMonth(), 10, 12)).toISOString();
            return {
                caseId: client.caseId,
                accountantId: client.accountantId,
                reference: e.reference,
                description: e.description,
                documentDate: toUtcNoonIso(e.date),
                uploadDate,
                reportingMonthDate: reportingIso,
                registrationMonthDate: reportingIso,
                debitAccount: e.debitAccount,
                creditAccount: e.creditAccount,
                sortCodeId: e.sortCodeId,
                amount: e.amount,
                vat: e.vat,
            };
        });

        setSaving(true);
        try {
            const res = await DocumentService.UploadDocumentPayloads('JournalEntries', payload, token);
            if (!res.ok) {
                const msg = await res.text();
                throw new Error(msg || 'Import failed');
            }
            await persistPicks();
            toast.success(`${t('ZReportImported') || 'Z report imported'} (${payload.length})`);
            onClose();
            if (onCreated) await onCreated();
        } catch (err) {
            toast.error(err?.message || t('ZReportImportFailed') || 'Failed to import the Z report');
        } finally {
            setSaving(false);
        }
    };

    const renderPicker = (picker) => {
        const isSort = picker.key === 'incomeSortCode';
        const options = isSort ? sortOptions : cardOptions;
        const value = picks[picker.key] || '';
        const required = isRequired(picker);
        const isSaved = !!value && value === savedPicks[picker.key];
        return (
            <div key={picker.key} className={modalStyles.formField}>
                <span className={modalStyles.fieldLabel}>
                    {t(picker.labelKey) || picker.fallback}
                    {required && <span className={modalStyles.requiredMark}> *</span>}
                </span>
                <Autocomplete
                    className={fieldStyles.combo}
                    size="small"
                    fullWidth
                    options={options}
                    value={options.find((o) => o.value === value) || null}
                    isOptionEqualToValue={(o, v) => o?.value === v?.value}
                    getOptionLabel={(o) => o?.label ?? ''}
                    onChange={(_, v) => setPicks((prev) => ({ ...prev, [picker.key]: v ? v.value : '' }))}
                    disabled={saving || loadingSettings}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            variant="standard"
                            fullWidth
                            placeholder={isSort ? (t('SelectSortCode') || 'Select sort code') : (t('SelectAccountCard') || 'Select account card')}
                            className={`${fieldStyles.styleInput}${required && !value ? ` ${fieldStyles.styleInputRequired}` : ''}`}
                            inputProps={{ ...params.inputProps, ...inputProps }}
                            InputProps={{ ...params.InputProps, ...comboInputProps }}
                        />
                    )}
                    ListboxProps={{ direction }}
                    PopperComponent={renderComboPopper}
                />
                {isSaved && (
                    <p className={modalStyles.fieldHint}>{t('ZReportSavedFromLastImport') || 'Saved from the last import'}</p>
                )}
            </div>
        );
    };

    const firstDate = rows.length ? formatUtcDate(rows[0].date) : '';
    const lastDate = rows.length ? formatUtcDate(rows[rows.length - 1].date) : '';
    const firstZ = rows.find((r) => r.zNumber)?.zNumber || '';
    const lastZ = [...rows].reverse().find((r) => r.zNumber)?.zNumber || '';

    return (
        <Portal>
            <div
                className={`${modalStyles.overlay} ${direction === 'rtl' ? modalStyles.rtl : modalStyles.ltr}`}
                onClick={saving ? undefined : onClose}
            >
                <div dir={direction} onClick={(e) => e.stopPropagation()}>
                    <div className={`${modalStyles.modal} ${styles.modalXL}`}>
                        <div className={modalStyles.body}>
                            <div className={modalStyles.header}>
                                <Tooltip title={t('Close') || 'Close'}>
                                    <IconButton onClick={onClose} className={modalStyles.closeIcon} disabled={saving}>
                                        <CancelIcon className={modalStyles.closeIcon} />
                                    </IconButton>
                                </Tooltip>
                                <div>{t('ImportZReport') || 'ייבוא דו"ח Z'}</div>
                                <div />
                            </div>

                            <div className={modalStyles.scroll}>
                                <div className={modalStyles.toolbar}>
                                    <p className={modalStyles.sectionTitle}>{t('ZReportCardsTitle') || 'Cards and sort code'}</p>
                                    {loadingSettings && <CircularProgress className={modalStyles.spinner} size={18} />}
                                </div>
                                <p className={modalStyles.muted} style={{ marginBottom: 10 }}>
                                    {t('ZReportCardsHint') || 'Your selection is saved for this client and filled in automatically on the next import.'}
                                </p>
                                <div className={modalStyles.formFields} style={{ marginBottom: 20 }}>
                                    {ALL_PICKERS.map(renderPicker)}
                                </div>

                                <input
                                    ref={fileInputRef}
                                    type="file"
                                    accept=".xls,.xlsx"
                                    style={{ display: 'none' }}
                                    onChange={(e) => handleFile(e.target.files?.[0])}
                                />

                                {!parsed ? (
                                    <div className={styles.methods}>
                                        <button
                                            type="button"
                                            className={styles.methodCard}
                                            onClick={() => fileInputRef.current?.click()}
                                            disabled={parsing}
                                        >
                                            <span className={styles.methodTitle}>
                                                {parsing ? <CircularProgress size={16} /> : <UploadFileIcon fontSize="small" />}
                                                {t('ZReportChooseFile') || 'Choose a Z report file (xls / xlsx)'}
                                            </span>
                                            <p className={styles.methodDesc}>
                                                {t('ZReportFileHint') || 'Upload the cash-register "Z summary report". Each Z creates an income entry and receipt entries per payment method.'}
                                            </p>
                                        </button>
                                    </div>
                                ) : (
                                    <>
                                        <div className={modalStyles.toolbar}>
                                            <p className={modalStyles.sectionTitle}>{t('Preview') || 'Preview'}</p>
                                            <button
                                                type="button"
                                                className={modalStyles.ghostBtn}
                                                onClick={() => fileInputRef.current?.click()}
                                                disabled={saving || parsing}
                                            >
                                                <UploadFileIcon fontSize="small" />
                                                {t('ChangeFile') || 'Change file'}
                                            </button>
                                        </div>

                                        <div className={styles.balance} style={{ marginTop: 0, marginBottom: 12 }}>
                                            <span>{fileName}</span>
                                            {parsed.companyId && <span>{t('ZReportCompanyId') || 'Company ID'}: {parsed.companyId}</span>}
                                            <span>{t('ZReportCount') || 'Z reports'}: {rows.length}</span>
                                            <span>{firstDate} – {lastDate}</span>
                                            {firstZ && <span>Z {firstZ}–{lastZ}</span>}
                                            <span>{t('Total') || 'Total'}: {beautifyNumber(totals.total)}</span>
                                            <span>{t('Cash') || 'Cash'}: {beautifyNumber(totals.cash)}</span>
                                            <span>{t('Checks') || 'Checks'}: {beautifyNumber(totals.checks)}</span>
                                            <span>{t('CreditCard') || 'Credit card'}: {beautifyNumber(totals.creditCard)}</span>
                                            {totals.exempt !== 0 && <span>{t('ZReportExempt') || 'Exempt'}: {beautifyNumber(totals.exempt)}</span>}
                                        </div>

                                        {mismatchRows.length > 0 && (
                                            <div className={styles.warning}>
                                                {t('ZReportMismatchWarning') || 'In these Z reports the payment methods do not add up to the total:'}{' '}
                                                {mismatchRows.map((r) => `${r.zNumber || formatUtcDate(r.date)} (${beautifyNumber(r.mismatch)})`).join(', ')}
                                            </div>
                                        )}
                                        {unbooked !== 0 && (
                                            <div className={styles.warning}>
                                                {t('ZReportUnbookedWarning') || 'Credit sales / other / vouchers / credit points are not booked as receipts and stay open in the customers card:'}{' '}
                                                {beautifyNumber(unbooked)}
                                            </div>
                                        )}
                                        {missingPickers.length > 0 && (
                                            <div className={styles.warning}>
                                                {t('ZReportMissingCards') || 'Choose the required cards before importing:'}{' '}
                                                {missingPickers.map((p) => t(p.labelKey) || p.fallback).join(', ')}
                                            </div>
                                        )}

                                        <div className={modalStyles.tabs} role="tablist">
                                            {[
                                                { value: Z_MODE_DAILY, label: t('ZReportModeDaily') || 'Entry per Z report (daily)' },
                                                { value: Z_MODE_MONTHLY, label: t('ZReportModeMonthly') || 'One summary entry per month' },
                                            ].map((m) => (
                                                <button
                                                    key={m.value}
                                                    type="button"
                                                    role="tab"
                                                    aria-selected={mode === m.value}
                                                    className={`${modalStyles.tab} ${mode === m.value ? modalStyles.tabActive : ''}`}
                                                    onClick={() => setMode(m.value)}
                                                    disabled={saving}
                                                >
                                                    {m.label}
                                                </button>
                                            ))}
                                        </div>

                                        <div className={modalStyles.tableWrap}>
                                            <TableContainer
                                                component={Paper}
                                                variant="outlined"
                                                sx={{ maxHeight: '46vh', '& .MuiTableCell-root': { py: 0.75, px: 1.25, textAlign, verticalAlign: 'middle' } }}
                                            >
                                                <Table size="small" stickyHeader dir={direction} sx={{ minWidth: 1000 }}>
                                                    <TableHead>
                                                        <TableRow>
                                                            <TableCell align={textAlign}>{t('Date') || 'Date'}</TableCell>
                                                            <TableCell align={textAlign}>{t('Reference') || 'Reference'}</TableCell>
                                                            <TableCell align={textAlign}>{t('Description') || 'Description'}</TableCell>
                                                            <TableCell align={textAlign}>{t('DebitAccount') || 'Debit account'}</TableCell>
                                                            <TableCell align={textAlign}>{t('CreditAccount') || 'Credit account'}</TableCell>
                                                            <TableCell align={textAlign}>{t('SortCode') || 'Sort code'}</TableCell>
                                                            <TableCell align={textAlign}>{t('VAT') || 'VAT'} %</TableCell>
                                                            <TableCell align={textAlign}>{t('Amount') || 'Amount'}</TableCell>
                                                        </TableRow>
                                                    </TableHead>
                                                    <TableBody>
                                                        {entries.map((e, i) => {
                                                            const groupStart = i === 0 || entries[i - 1].groupIndex !== e.groupIndex;
                                                            const groupShade = e.groupIndex % 2 === 1 ? '#fafbff' : undefined;
                                                            return (
                                                                <TableRow
                                                                    key={`${e.groupIndex}-${e.kind}`}
                                                                    sx={{
                                                                        backgroundColor: groupShade,
                                                                        '& td': groupStart && i > 0 ? { borderTop: '2px solid #e5e7f6' } : undefined,
                                                                    }}
                                                                >
                                                                    <TableCell align={textAlign}>{groupStart ? formatUtcDate(e.date) : ''}</TableCell>
                                                                    <TableCell align={textAlign}>{groupStart ? e.reference : ''}</TableCell>
                                                                    <TableCell align={textAlign}>{e.description}</TableCell>
                                                                    <TableCell align={textAlign} sx={{ color: e.debitAccount ? undefined : 'error.main' }}>
                                                                        {cardLabel(e.debitAccount)}
                                                                    </TableCell>
                                                                    <TableCell align={textAlign} sx={{ color: e.creditAccount ? undefined : 'error.main' }}>
                                                                        {cardLabel(e.creditAccount)}
                                                                    </TableCell>
                                                                    <TableCell align={textAlign} sx={{ color: e.sortCodeId ? undefined : 'error.main' }}>
                                                                        {sortLabel(e.sortCodeId)}
                                                                    </TableCell>
                                                                    <TableCell align={textAlign}>{e.vat}</TableCell>
                                                                    <TableCell align={textAlign} sx={{ fontWeight: 600 }}>{beautifyNumber(e.amount)}</TableCell>
                                                                </TableRow>
                                                            );
                                                        })}
                                                    </TableBody>
                                                </Table>
                                            </TableContainer>
                                        </div>

                                        <div className={`${styles.balance} ${missingPickers.length ? styles.balanceError : styles.balanceOk}`}>
                                            <span>{t('ZReportEntriesToCreate') || 'Journal entries to create'}: {entries.length}</span>
                                            <span>{t('ZReportIncomeTotal') || 'Income'}: {beautifyNumber(totals.total)}</span>
                                            <span>{t('ZReportReceiptsTotal') || 'Receipts'}: {beautifyNumber(receiptsTotal)}</span>
                                        </div>
                                    </>
                                )}
                            </div>

                            <div className={modalStyles.footer}>
                                <button type="button" className={modalStyles.secondaryBtn} onClick={onClose} disabled={saving}>
                                    {t('cancelButton') || 'Cancel'}
                                </button>
                                <button
                                    type="button"
                                    className={modalStyles.primaryBtn}
                                    disabled={!canImport}
                                    onClick={handleImport}
                                >
                                    {saving
                                        ? <CircularProgress className={modalStyles.spinner} size={18} color="inherit" />
                                        : `${t('Import') || 'Import'}${entries.length ? ` (${entries.length})` : ''}`}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </Portal>
    );
};

const ZReportImportModal = ({ open, ...props }) => (open ? <ZReportImportModalContent {...props} /> : null);

export default ZReportImportModal;
